import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  styled,
  Typography,
} from "@mui/material";
import _ from "lodash";
import { ReactElement } from "react";

import { CampaignSampleContact } from "~frontend/__generated__/graphql";
import theme, { palette } from "~frontend/theme";
import { spacingArray } from "~frontend/util/styling";

import { CampaignForEditor } from "../types";

const Accordion = styled(MuiAccordion)({
  padding: 0,
  margin: "-1px 0 0",
  border: `1px solid ${palette.midGray}`,
  "&.Mui-expanded": {
    minHeight: "auto",
    margin: "-1px 0 0",
  },
  "& .MuiAccordionSummary-root.Mui-expanded": {
    minHeight: "auto",
  },
  "& .MuiAccordionSummary-content.Mui-expanded": {
    minHeight: "auto",
    margin: 0,
  },
});

const AccordionSummary = styled(MuiAccordionSummary)({
  padding: 0,
  margin: 0,
  backgroundColor: palette.white,
  minHeight: "auto",
  "& .MuiSvgIcon-root": {
    width: "1em",
    height: "1em",
    marginRight: "15px",
  },
  "& > .MuiAccordionSummary-content": {
    margin: 0,
    padding: 0,
  },
  "& .Mui-expanded": {
    minHeight: "auto",
    margin: 0,
    padding: 0,
  },
});

const AccordionDetails = styled(MuiAccordionDetails)({
  padding: spacingArray([1, 0, 2, 2]),
  backgroundColor: palette.white,
});

export default function ContactListPreview({
  campaign,
}: {
  campaign: CampaignForEditor;
}): ReactElement {
  const sampleContacts = campaign?.availableSampleContacts || [];
  const tableData = sampleContacts.map((contact: CampaignSampleContact) => {
    const dataObj = JSON.parse(contact.data);
    return {
      id: contact.id,
      phoneNumber: contact.phoneNumber,
      ...dataObj,
    };
  });

  if (sampleContacts.length === 0) {
    return <></>;
  }

  // We build the list of columns by combining the keys from the data attribute
  // on each sample contact (in case the contacts have inconsistent data keys).
  const columns = _.uniq(
    _.flatMap(tableData, row => Object.keys(row)).filter(
      (k: string) => k != "id"
    )
  );

  return (
    <Accordion square={true}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography
          variant="body2Bold"
          component="span"
          sx={{ paddingLeft: theme.spacing(2) }}
        >
          List Preview
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer sx={{ maxWidth: "lg", overflowX: "auto" }}>
          <Table size="small" aria-label="Preview of the imported contacts">
            <TableHead>
              <TableRow key={`contactListPreviewHeaderRow${campaign.id}`}>
                {columns.map((column: string) => (
                  <TableCell
                    key={`contactListPreviewHeaderCell${column}`}
                    sx={{ paddingLeft: 0, textWrap: "nowrap" }}
                  >
                    <Typography
                      variant="body2Bold"
                      sx={{ color: palette.darkGray }}
                      component="span"
                    >
                      {column}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map(row => (
                <TableRow
                  key={`contactListPreviewRow-${row.id}-${row.phoneNumber}`}
                >
                  {columns.map((key: string) => (
                    <TableCell
                      key={`contactListPreviewCell${row.id}-${key}`}
                      sx={{ paddingLeft: 0 }}
                    >
                      {row[key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
}
